// src/pages/BlockedIps.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Ban, Plus, RefreshCw, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import {
  createBlockedIp,
  deleteBlockedIp,
  getBlockedIps,
  getMyScripts,
  patchBlockedIp,
} from "../services/blockedIps";

function fmtDate(dt) {
  if (!dt) return "-";
  try {
    const d = new Date(dt);
    return d.toLocaleString("pt-BR");
  } catch {
    return String(dt);
  }
}

export default function BlockedIps() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [items, setItems] = useState([]);
  const [scripts, setScripts] = useState([]);

  // filtros (listagem)
  const [fScope, setFScope] = useState("");
  const [fActive, setFActive] = useState(""); // "true" | "false" | ""
  const [fScriptUuid, setFScriptUuid] = useState("");

  // form (criar)
  const [ip, setIp] = useState("");
  const [scope, setScope] = useState("global"); // global | script
  const [scriptUuid, setScriptUuid] = useState("");
  const [note, setNote] = useState("");

  const scriptMap = useMemo(() => {
    const m = new Map();
    (Array.isArray(scripts) ? scripts : []).forEach((s) => {
      const name = s?.name || s?.project_name || s?.title || s?.script_name || s?.uuid;
      if (s?.uuid) m.set(s.uuid, name);
    });
    return m;
  }, [scripts]);

  const filteredItems = useMemo(() => {
    return (Array.isArray(items) ? items : []).filter((row) => {
      const rowScope = String(row?.scope || "").toLowerCase();
      const rowActive = !!row?.is_active;
      const rowScriptUuid =
        row?.script_uuid ||
        row?.script?.uuid ||
        row?.script_uuid_fk ||
        "";

      const matchScope = !fScope || rowScope === fScope;
      const matchActive =
        fActive === ""
          ? true
          : fActive === "true"
          ? rowActive === true
          : rowActive === false;

      const matchScript =
        !fScriptUuid || String(rowScriptUuid) === String(fScriptUuid);

      return matchScope && matchActive && matchScript;
    });
  }, [items, fScope, fActive, fScriptUuid]);

  async function loadAll() {
    setLoading(true);
    setError("");
    try {
      const [scriptsRes, blocksRes] = await Promise.all([
        getMyScripts().catch(() => []),
        getBlockedIps({}).catch(() => []),
      ]);

      const scriptsList = Array.isArray(scriptsRes)
        ? scriptsRes
        : (scriptsRes?.items || scriptsRes?.data || []);

      const blocksList = Array.isArray(blocksRes)
        ? blocksRes
        : (blocksRes?.items || blocksRes?.data || []);

      setScripts(scriptsList);
      setItems(blocksList);
    } catch (e) {
      setError(e?.message || "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function applyFilters() {
    setLoading(true);
    setError("");
    try {
      const blocksRes = await getBlockedIps({});
      const blocksList = Array.isArray(blocksRes)
        ? blocksRes
        : (blocksRes?.items || blocksRes?.data || []);
      setItems(blocksList);
    } catch (e) {
      setError(e?.message || "Erro ao filtrar");
    } finally {
      setLoading(false);
    }
  }

  async function onCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ip: ip.trim(),
        scope,
        script_uuid: scope === "script" ? (scriptUuid || "").trim() : "",
        note: (note || "").trim(),
      };

      await createBlockedIp(payload);

      setIp("");
      setNote("");
      if (scope === "global") setScriptUuid("");

      await loadAll();
    } catch (e2) {
      setError(e2?.message || "Erro ao criar bloqueio");
    } finally {
      setSaving(false);
    }
  }

  async function onToggle(row) {
    const next = !row?.is_active;
    try {
      await patchBlockedIp(row.id, { is_active: next });
      await loadAll();
    } catch (e) {
      setError(e?.message || "Erro ao atualizar status");
    }
  }

  async function onDelete(row) {
    try {
      await deleteBlockedIp(row.id);
      await loadAll();
    } catch (e) {
      setError(e?.message || "Erro ao remover");
    }
  }

  // Helper para extrair dados da row de forma consistente
  function getRowData(row) {
    const sc = String(row.scope || "").toLowerCase();
    const scr =
      row.script_uuid ||
      row.script?.uuid ||
      row.script_uuid_fk ||
      "";

    const scriptName =
      row.script_name ||
      row.script?.name ||
      row.script?.project_name ||
      row.script?.title ||
      (scr ? scriptMap.get(scr) : "") ||
      (sc === "script" ? scr : "-");

    const active = !!row.is_active;

    return { sc, scr, scriptName, active };
  }

  return (
    <div className="p-4 md:p-6 mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ban className="w-6 h-6" /> Bloqueio de IPs
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Bloqueie IPs por <b>global</b> ou por <b>SobeScript</b>. Quando bloqueado, o script redireciona para 404 no próprio domínio.
          </p>
        </div>

        <button
          onClick={loadAll}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 shrink-0"
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Atualizar</span>
        </button>
      </div>

      {error ? (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      ) : null}

      {/* FORM */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 mb-6">
        <h2 className="font-semibold mb-3">Adicionar bloqueio</h2>

        <form onSubmit={onCreate} className="grid md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">IP</label>
            <input
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              placeholder="ex: 123.45.67.89"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">Escopo</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
            >
              <option value="global">global</option>
              <option value="script">script</option>
            </select>
          </div>

          <div className="md:col-span-4">
            <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">
              Script (se escopo=script)
            </label>

            <select
              value={scriptUuid}
              onChange={(e) => setScriptUuid(e.target.value)}
              disabled={scope !== "script"}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent disabled:opacity-50"
            >
              <option value="">Selecione…</option>
              {scripts.map((s) => (
                <option key={s.uuid} value={s.uuid}>
                  {s.name || s.project_name || s.title || s.script_name || s.uuid}
                </option>
              ))}
            </select>

            {scope === "script" && !scriptUuid ? (
              <div className="text-[11px] text-amber-600 mt-1">
                Selecione um script para bloquear apenas nele.
              </div>
            ) : null}
          </div>

          <div className="md:col-span-3">
            <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">Nota (opcional)</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ex: spy / QA / agência"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
            />
          </div>

          <div className="md:col-span-12 flex items-center justify-end">
            <button
              type="submit"
              disabled={saving || (scope === "script" && !scriptUuid)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
            >
              <Plus className="w-4 h-4" />
              {saving ? "Salvando..." : "Bloquear IP"}
            </button>
          </div>
        </form>
      </div>

      {/* FILTROS */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 mb-3">
        <div className="grid grid-cols-2 md:flex md:flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">Filtro: escopo</label>
            <select
              value={fScope}
              onChange={(e) => setFScope(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
            >
              <option value="">Todos</option>
              <option value="global">global</option>
              <option value="script">script</option>
            </select>
          </div>

          <div>
            <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">Filtro: ativo</label>
            <select
              value={fActive}
              onChange={(e) => setFActive(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
            >
              <option value="">Todos</option>
              <option value="true">ativos</option>
              <option value="false">inativos</option>
            </select>
          </div>

          <div className="col-span-2 md:col-span-1 md:min-w-[260px]">
            <label className="block text-xs mb-1 text-slate-600 dark:text-slate-300">Filtro: script</label>
            <select
              value={fScriptUuid}
              onChange={(e) => setFScriptUuid(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent"
            >
              <option value="">Todos</option>
              {scripts.map((s) => (
                <option key={s.uuid} value={s.uuid}>
                  {s.name || s.project_name || s.title || s.script_name || s.uuid}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={applyFilters}
            className="col-span-2 md:col-span-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            disabled={loading}
          >
            Filtrar
          </button>
        </div>
      </div>

      {/* LISTA — CARDS NO MOBILE / TABELA NO DESKTOP */}

      {/* Mobile: Cards (visível < md) */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500">
            Carregando...
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500">
            Nenhum IP bloqueado encontrado.
          </div>
        ) : (
          filteredItems.map((row) => {
            const { sc, scriptName, active } = getRowData(row);
            return (
              <div
                key={row.id}
                className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
              >
                {/* Cabeçalho do card: IP + status */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">IP</div>
                    <div className="font-mono text-sm break-all">{row.ip}</div>
                  </div>
                  <span
                    className={`shrink-0 px-2 py-1 rounded-full text-xs font-semibold ${
                      active
                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                        : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                    }`}
                  >
                    {active ? "Ativo" : "Inativo"}
                  </span>
                </div>

                {/* Detalhes em grid de 2 colunas */}
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Escopo</div>
                    <div className="break-words">{sc || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Script</div>
                    <div className="break-words">{sc === "script" ? scriptName || "-" : "-"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Nota</div>
                    <div className="break-words">{row.note || "-"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Criado em</div>
                    <div className="text-slate-600 dark:text-slate-300">{fmtDate(row.created_at)}</div>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => onToggle(row)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    {active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    {active ? "Desativar" : "Ativar"}
                  </button>

                  <button
                    onClick={() => onDelete(row)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remover
                  </button>
                </div>

                <div className="text-[11px] text-slate-400 text-right mt-2">ID: {row.id}</div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop: Tabela (visível >= md) */}
      <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/40">
              <tr className="text-left">
                <th className="px-4 py-3 w-[24%]">IP</th>
                <th className="px-4 py-3 w-[10%]">Escopo</th>
                <th className="px-4 py-3 w-[18%]">Script</th>
                <th className="px-4 py-3 w-[18%]">Nota</th>
                <th className="px-4 py-3 w-[8%]">Ativo</th>
                <th className="px-4 py-3 w-[12%]">Criado</th>
                <th className="px-4 py-3 w-[10%] text-right">Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-4 text-slate-500" colSpan={7}>
                    Carregando...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td className="px-4 py-4 text-slate-500" colSpan={7}>
                    Nenhum IP bloqueado encontrado.
                  </td>
                </tr>
              ) : (
                filteredItems.map((row) => {
                  const { sc, scriptName, active } = getRowData(row);

                  return (
                    <tr key={row.id} className="border-t border-slate-100 dark:border-slate-800 align-top">
                      <td className="px-4 py-3 font-mono break-all text-xs md:text-sm">
                        {row.ip}
                      </td>

                      <td className="px-4 py-3 whitespace-normal break-words">
                        {sc || "-"}
                      </td>

                      <td className="px-4 py-3 whitespace-normal break-words">
                        {sc === "script" ? scriptName || "-" : "-"}
                      </td>

                      <td className="px-4 py-3 whitespace-normal break-words">
                        {row.note || "-"}
                      </td>

                      <td className="px-4 py-3">
                        <span className={active ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                          {active ? "Sim" : "Não"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-slate-500 whitespace-normal break-words">
                        {fmtDate(row.created_at)}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-col items-end gap-2">
                          <button
                            onClick={() => onToggle(row)}
                            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 whitespace-nowrap"
                            title="Ativar/Desativar"
                          >
                            {active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            {active ? "Ativo" : "Inativo"}
                          </button>

                          <button
                            onClick={() => onDelete(row)}
                            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 whitespace-nowrap"
                            title="Remover"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remover
                          </button>

                          <div className="text-[11px] text-slate-500 text-right break-all">
                            ID: {row.id}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* COMO TESTAR */}
      <div className="mt-5 text-sm text-slate-600 dark:text-slate-300">
        <b>Como testar agora:</b>
        <ol className="list-decimal ml-5 mt-2 space-y-1">
          <li>Adicione o seu IP global ou por script.</li>
          <li>Use o filtro por script e confira se a lista reduz corretamente.</li>
          <li>Nos registros com escopo script, agora deve aparecer o nome do projeto.</li>
          <li>Abra a página que tem esse SobeScript instalado.</li>
          <li>Ao carregar o script, você deve cair em um <b>404</b> no mesmo domínio.</li>
        </ol>
      </div>
    </div>
  );
}